import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import './knowledge/modern-theme.css';
import './knowledge/design-v3.css';
import KBContext from './knowledge/KBContext';
import { ThemeProvider, useVisualTheme } from './knowledge/ThemeContext';
import LoginScreen from './knowledge/LoginScreen';
import KBLoadingScreen from './knowledge/KBLoadingScreen';
import KBSidebar from './knowledge/KBSidebar';
import TopNav from './knowledge/TopNav';
import ChatPanel from './knowledge/ChatPanel';
import DocumentsPanel from './knowledge/DocumentsPanel';
import MembersPanel from './knowledge/MembersPanel';
import AdminPanel from './knowledge/AdminPanel';
import DisclaimerModal from './knowledge/DisclaimerModal';
import QuickGuideModal from './knowledge/QuickGuideModal';
import ProfileEditModal from './knowledge/ProfileEditModal';
import MemberWelcomeModal from './knowledge/MemberWelcomeModal';
import IngestionProgress from './knowledge/IngestionProgress';

const KnowledgeBasePageInner = () => {
  const { visualTheme } = useVisualTheme();

  // --- Auth state ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingDocId, setDeletingDocId] = useState(null);
  const [password, setPassword] = useState(() => {
    try { return sessionStorage.getItem('ipmi_staff_pw') || ''; } catch (e) { return ''; }
  });
  const [passwordError, setPasswordError] = useState('');

  // --- Member magic-link auth state (Phase 1C) ---
  const [memberInfo, setMemberInfo] = useState(null);
  const [magicEmail, setMagicEmail] = useState('');
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  const [magicError, setMagicError] = useState('');
  const [showLegacyPassword, setShowLegacyPassword] = useState(false);  // collapse the access-password form by default
  const [showProfileModal, setShowProfileModal] = useState(false);  // member self-edit profile modal
  const [showMemberWelcome, setShowMemberWelcome] = useState(false);  // first-run welcome for members
  const [adminFocus, setAdminFocus] = useState(null);  // signal to focus a section in Admin Tools (e.g. 'org-profile')

  const [activeTab, setActiveTab] = useState('chat');

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isQuerying, setIsQuerying] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  // Document edit state
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [editorName, setEditorName] = useState('');

  // Ingestion state
  const [ingestJobId, setIngestJobId] = useState(null);
  const [ingestStatus, setIngestStatus] = useState(null);
  const [ingestDone, setIngestDone] = useState(null);
  const [ingestionRunning, setIngestionRunning] = useState(false);

  // Filter state
  const [filterOptions, setFilterOptions] = useState({ categories: [], years: [], file_types: [], authors: [], organizations: [], key_topics: [] });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeYears, setActiveYears] = useState([]);
  const [activeFileTypes, setActiveFileTypes] = useState([]);
  const [activeTopics, setActiveTopics] = useState([]);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [sortBy, setSortBy] = useState('title');
  const [sortOrder, setSortOrder] = useState('asc');
  const [lightMode, setLightMode] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  // Suggestion state
  const [suggestionText, setSuggestionText] = useState('');
  const [suggestionSent, setSuggestionSent] = useState(false);
  const [sendingSuggestion, setSendingSuggestion] = useState(false);
  const [showSuggestionBox, setShowSuggestionBox] = useState(false);
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);

  // Chat feedback & doc card expand state
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const [expandedDocCards, setExpandedDocCards] = useState({});

  // Trending, Hot Picks state
  const [trendingData, setTrendingData] = useState(null);
  const [hotPicks, setHotPicks] = useState([]);
  const [hotPicksIdx, setHotPicksIdx] = useState(0);
  const [relatedQueries, setRelatedQueries] = useState({});

  // Upload in admin
  const [adminFiles, setAdminFiles] = useState([]);
  const [adminUploading, setAdminUploading] = useState(false);
  const [adminUploadProgress, setAdminUploadProgress] = useState({ current: 0, total: 0, succeeded: 0, failed: 0 });
  const [adminCapWarning, setAdminCapWarning] = useState('');
  const [uploadConference, setUploadConference] = useState('');
  const [uploadYear, setUploadYear] = useState('');

  // Admin state
  const [queryLogs, setQueryLogs] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [backendVersion, setBackendVersion] = useState(null);
  const [loginVersion, setLoginVersion] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const pollRef = useRef(null);

  // --- Effects ---
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    fetch(`${API_URL}/api/kb/version`).then(r => r.ok ? r.json() : null).then(d => { if (d) setLoginVersion(d.version); }).catch(() => {});
  }, [API_URL]);

  // --- Member session auto-detect ---
  // On mount, check if the browser has a valid member session cookie. If yes,
  // skip the login screen entirely. The legacy "password" gate that all backend
  // routes still use is auto-populated for transparent operation; this shim will
  // go away in Phase 1D when routes accept the session cookie directly.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Same-origin relative path (NOT REACT_APP_BACKEND_URL). The httpOnly
        // ipmi_member_session cookie is set first-party on the app's own domain
        // (ai.ipmi.org) by the /auth/magic POST, so it's only sent on same-origin
        // requests. In production REACT_APP_BACKEND_URL points at the platform
        // host (golden-*.emergent.host) — using it here would be cross-origin and
        // the cookie would be dropped, bouncing the member back to login.
        const res = await fetch('/api/auth/member/me', { credentials: 'include' });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setMemberInfo(data.member);
          // First-run welcome — show once per member (per browser)
          try {
            const wkey = `ipmi_welcomed_${data.member?.member_id || data.member?.email || 'm'}`;
            if (!localStorage.getItem(wkey)) setShowMemberWelcome(true);
          } catch (e) { /* storage unavailable — skip */ }
          // Use the legacy gate password as the in-flight "access" token.
          // Backend routes will be updated to read the member cookie in Phase 1D.
          const SHIM_PW = 'IPMI2026!@#$%';
          setPassword(SHIM_PW);
          setIsAuthenticated(true);
          // Pass the shim explicitly — state update above is async and the fetch
          // closures would otherwise see the stale (empty) password and 401.
          Promise.all([
            fetchDocuments({ password: SHIM_PW }),
            fetchFilters(SHIM_PW),
            fetchTrending(SHIM_PW),
          ]).finally(() => setAppReady(true));
          // Clean ?welcome=1 from URL if present (set by /verify-magic-link)
          if (window.location.search.includes('welcome=1')) {
            window.history.replaceState({}, '', window.location.pathname);
          }
        }
      } catch (err) { /* not a member (or /me blocked) — staff restore handled below */ }
    })();
    return () => { cancelled = true; };
    // We intentionally run this only once on mount.
  }, []);  // eslint-disable-line

  // Restore a persisted staff/admin access-password session on refresh.
  // Runs independently of the member /me check (which can throw on credentialed
  // CORS), and is resilient to React StrictMode double-invocation because it only
  // guards the final setState calls — isAuthenticated was already restored
  // synchronously in useState above, so the login screen is never shown.
  useEffect(() => {
    let staffPw = null;
    try { staffPw = sessionStorage.getItem('ipmi_staff_pw'); } catch (e) { /* storage off */ }
    if (!staffPw) return;
    let cancelled = false;
    (async () => {
      try {
        const fd = new FormData();
        fd.append('password', staffPw);
        const vr = await fetch(`${API_URL}/api/kb/verify-password`, { method: 'POST', body: fd });
        if (cancelled) return;
        if (vr.ok) {
          setIsAuthenticated(true);
          setPassword(staffPw);
          Promise.all([
            fetchDocuments({ password: staffPw }),
            fetchFilters(staffPw),
            fetchTrending(staffPw),
          ]).finally(() => { if (!cancelled) setAppReady(true); });
        } else {
          // Stored password no longer valid — clear it and show login.
          try { sessionStorage.removeItem('ipmi_staff_pw'); } catch (e) { /* storage off */ }
          setIsAuthenticated(false);
          setPassword('');
        }
      } catch (err) {
        // Transient/network error validating — load optimistically rather than
        // locking the user out (they were authenticated this browser session).
        if (cancelled) return;
        Promise.all([
          fetchDocuments({ password: staffPw }),
          fetchFilters(staffPw),
          fetchTrending(staffPw),
        ]).finally(() => { if (!cancelled) setAppReady(true); });
      }
    })();
    return () => { cancelled = true; };
  }, []);  // eslint-disable-line

  const requestMagicLink = async (e) => {
    e?.preventDefault?.();
    setMagicError('');
    if (!magicEmail || !magicEmail.includes('@')) {
      setMagicError('Please enter a valid email address.');
      return;
    }
    setMagicSending(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/member/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: magicEmail.trim() }),
      });
      if (res.ok) {
        setMagicSent(true);
      } else {
        const data = await res.json().catch(() => ({}));
        setMagicError(data.detail || 'Could not send link. Please try again.');
      }
    } catch (err) {
      setMagicError('Connection error. Please try again.');
    }
    setMagicSending(false);
  };

  // Member self-edit profile save
  const updateMemberProfile = async (payload) => {
    try {
      // Same-origin relative path so the member-session cookie is sent (see /me note).
      const res = await fetch('/api/auth/member/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        setMemberInfo(data.member);
        return { ok: true, member: data.member };
      }
      const data = await res.json().catch(() => ({}));
      return { ok: false, error: data.detail || 'Could not save changes.' };
    } catch (err) {
      return { ok: false, error: 'Connection error. Please try again.' };
    }
  };

  const memberLogout = async () => {
    try {
      // Same-origin relative path so the member-session cookie is cleared (see /me note).
      await fetch('/api/auth/member/logout', { method: 'POST', credentials: 'include' });
    } catch (err) { console.error('logout error', err); }
    setMemberInfo(null);
    setIsAuthenticated(false);
    setPassword('');
    setMagicEmail('');
    setMagicSent(false);
    window.location.href = '/';
  };

  // Staff/admin (access-password) sign-out — clears the persisted browser-session
  // gate and returns to the login screen.
  const staffLogout = () => {
    try { sessionStorage.removeItem('ipmi_staff_pw'); } catch (e) { /* storage off */ }
    setIsAuthenticated(false);
    setPassword('');
    setAppReady(false);
    setActiveTab('chat');
    setMessages([]);
    setSessionId(null);
  };

  const closeMemberWelcome = () => {
    setShowMemberWelcome(false);
    try {
      const id = memberInfo?.member_id || memberInfo?.email || 'm';
      localStorage.setItem(`ipmi_welcomed_${id}`, '1');
    } catch (e) { /* storage unavailable — skip */ }
  };

  useEffect(() => {
    if (!ingestJobId) return;
    let notFoundCount = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/kb/ingest-status/${ingestJobId}`);
        if (res.status === 404) {
          notFoundCount++;
          if (notFoundCount >= 3) {
            // Job lost (server restarted during ingestion)
            clearInterval(pollRef.current);
            pollRef.current = null;
            setIngestJobId(null);
            setIngestDone({ status: 'failed', message: 'Ingestion job was lost — the server may have restarted. Please try syncing again.', processed: 0, skipped: 0, errors: [] });
            setIngestionRunning(false);
          }
          return;
        }
        if (!res.ok) return;
        notFoundCount = 0;
        const data = await res.json();
        setIngestStatus(data);
        setIngestionRunning(!!data.ingestion_active);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIngestJobId(null);
          setIngestDone(data);
          setIngestionRunning(false);
          fetchDocuments();
          fetchFilters();
          setTimeout(() => { setIngestDone(null); setIngestStatus(null); }, 6000);
        }
      } catch (err) { console.error('Poll error:', err); }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [ingestJobId]);

  useEffect(() => {
    if (isAuthenticated) fetchDocuments();
  }, [activeCategories, activeYears, activeFileTypes, activeTopics, sortBy, sortOrder, isAuthenticated]);

  useEffect(() => {
    // Admin data is staff/admin-only — never fetch it for a signed-in member.
    if (activeTab === 'admin' && isAuthenticated && !memberInfo) fetchAdminData();
  }, [activeTab, isAuthenticated, memberInfo]);

  // --- API Functions ---
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    try {
      const formData = new FormData();
      formData.append('password', password);
      const response = await fetch(`${API_URL}/api/kb/verify-password`, { method: 'POST', body: formData });
      if (response.ok) {
        setIsAuthenticated(true);
        try { sessionStorage.setItem('ipmi_staff_pw', password); } catch (e) { /* storage off */ }
        Promise.all([fetchDocuments(), fetchFilters(), fetchTrending()]).finally(() => setAppReady(true));
      } else {
        setPasswordError('Invalid password');
      }
    } catch (err) {
      console.error('[access-password] verify failed:', err);
      setPasswordError('Connection error. Please try again.');
    }
  };

  const fetchFilters = useCallback(async (pwOverride) => {
    try {
      const response = await fetch(`${API_URL}/api/kb/filters`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: pwOverride || password })
      });
      if (response.ok) setFilterOptions(await response.json());
    } catch (err) { console.error('Failed to fetch filters:', err); }
  }, [API_URL, password]);

  const fetchDocuments = useCallback(async (overrides = {}) => {
    setLoadingDocs(true);
    try {
      const cats = 'categories' in overrides ? overrides.categories : activeCategories;
      const yrs = 'years' in overrides ? overrides.years : activeYears;
      const fts = 'file_types' in overrides ? overrides.file_types : activeFileTypes;
      const topics = 'key_topics' in overrides ? overrides.key_topics : activeTopics;
      const body = {
        password: overrides.password || password,
        search: ('search' in overrides ? overrides.search : searchQuery) || undefined,
        category: cats.length > 0 ? cats : undefined,
        year: yrs.length > 0 ? yrs.map(Number) : undefined,
        file_type: fts.length > 0 ? fts : undefined,
        key_topics: topics.length > 0 ? topics : undefined,
        sort_by: overrides.sort_by || sortBy,
        sort_order: overrides.sort_order || sortOrder
      };
      Object.keys(body).forEach(k => body[k] === undefined && delete body[k]);
      const response = await fetch(`${API_URL}/api/kb/documents`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) { console.error('Failed to fetch documents:', err); }
    finally { setLoadingDocs(false); }
  }, [API_URL, password, searchQuery, activeCategories, activeYears, activeFileTypes, activeTopics, sortBy, sortOrder]);

  const fetchTrending = useCallback(async (pwOverride) => {
    const pw = pwOverride || password;
    try {
      const [tRes, hRes] = await Promise.all([
        fetch(`${API_URL}/api/kb/trending`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: pw }) }),
        fetch(`${API_URL}/api/kb/hot-picks`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password: pw }) })
      ]);
      if (tRes.ok) setTrendingData(await tRes.json());
      if (hRes.ok) { const d = await hRes.json(); setHotPicks(d.sets || []); }
    } catch (e) { console.error('Trending fetch failed:', e); }
  }, [API_URL, password]);

  const fetchRelatedQueries = useCallback(async (question, msgIdx) => {
    try {
      const res = await fetch(`${API_URL}/api/kb/related-queries`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question, password })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.related?.length > 0) {
          setRelatedQueries(prev => ({ ...prev, [msgIdx]: data.related }));
        }
      }
    } catch (e) { /* silent */ }
  }, [API_URL, password]);

  const refreshHotPicks = () => {
    setHotPicksIdx(prev => (prev + 1) % Math.max(hotPicks.length, 1));
    fetchTrending();
  };

  const handleAdminFileSelect = (files) => {
    const MAX_FILES = 100;
    const fileArr = Array.from(files);
    if (fileArr.length > MAX_FILES) {
      setAdminCapWarning(`Selected ${fileArr.length} files. Maximum is ${MAX_FILES}. Only the first ${MAX_FILES} will be uploaded.`);
      setAdminFiles(fileArr.slice(0, MAX_FILES));
    } else {
      setAdminCapWarning('');
      setAdminFiles(fileArr);
    }
  };

  const findDuplicates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/kb/find-duplicates`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) { const data = await res.json(); setDuplicates(data.duplicates); }
    } catch (err) { console.error('Find duplicates failed:', err); }
  };

  const handleDeleteDoc = async (docId) => {
    if (!deletePassword) return;
    setDeletingDocId(docId);
    try {
      const res = await fetch(`${API_URL}/api/kb/delete-document`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, delete_password: deletePassword, doc_id: docId })
      });
      if (res.ok) {
        setDuplicates(prev => prev.map(g => ({
          ...g, docs: g.docs.filter(d => d.doc_id !== docId), count: g.count - 1
        })).filter(g => g.count > 1));
        fetchDocuments();
      } else {
        const err = await res.json();
        alert(err.detail || 'Delete failed');
      }
    } catch (err) { alert('Delete failed: ' + err.message); }
    setDeletingDocId(null);
  };

  const handleAdminUpload = async () => {
    if (!adminFiles.length || adminUploading) return;
    setAdminUploading(true);
    const total = adminFiles.length;
    let succeeded = 0, failed = 0;
    setAdminUploadProgress({ current: 0, total, succeeded: 0, failed: 0 });
    const BATCH = 3;
    for (let i = 0; i < total; i += BATCH) {
      const batch = adminFiles.slice(i, i + BATCH);
      await Promise.all(batch.map(async (file) => {
        try {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('password', password);
          if (uploadConference) fd.append('conference', uploadConference);
          if (uploadYear) fd.append('year', uploadYear);
          const res = await fetch(`${API_URL}/api/r2/upload`, { method: 'POST', body: fd });
          if (res.ok) succeeded++; else failed++;
        } catch { failed++; }
      }));
      setAdminUploadProgress({ current: Math.min(i + BATCH, total), total, succeeded, failed });
    }
    setAdminUploading(false);
    setAdminFiles([]);
  };

  const handleSearchSubmit = (e) => { e.preventDefault(); fetchDocuments(); };

  const clearFilters = () => {
    setSearchQuery('');
    setActiveCategories([]);
    setActiveYears([]);
    setActiveFileTypes([]);
    setActiveTopics([]);
    setSortBy('title');
    setSortOrder('asc');
    fetchDocuments({ search: '', categories: [], years: [], file_types: [], key_topics: [], sort_by: 'title', sort_order: 'asc' });
  };

  const hasActiveFilters = searchQuery || activeCategories.length > 0 || activeYears.length > 0 || activeFileTypes.length > 0 || activeTopics.length > 0;

  const toggleCategory = (cat) => setActiveCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  const toggleYear = (yr) => { const s = String(yr); setActiveYears(prev => prev.includes(s) ? prev.filter(y => y !== s) : [...prev, s]); };
  const toggleFileType = (ft) => setActiveFileTypes(prev => prev.includes(ft) ? prev.filter(f => f !== ft) : [...prev, ft]);
  const toggleTopic = (topic) => setActiveTopics(prev => prev.includes(topic) ? prev.filter(t => t !== topic) : [...prev, topic]);

  const handleSyncNew = async () => {
    if (ingestJobId) return;
    if (!window.confirm('Are you sure you want to sync from R2?\n\nOnly sync when new documents have been added — each sync uses AI processing credits.')) return;
    setIngestDone(null);
    setIngestStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/kb/ingest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (data.job_id) setIngestJobId(data.job_id);
    } catch (err) {
      setIngestDone({ status: 'failed', message: `Network error: ${err.message}`, processed: 0, skipped: 0, errors: [] });
    }
  };

  const handleReingest = async () => {
    if (ingestJobId) return;
    setIngestDone(null);
    setIngestStatus(null);
    try {
      const response = await fetch(`${API_URL}/api/kb/reingest`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json();
      if (data.job_id) setIngestJobId(data.job_id);
    } catch (err) {
      setIngestDone({ status: 'failed', message: `Network error: ${err.message}`, processed: 0, skipped: 0, errors: [] });
    }
  };

  const isIngesting = !!ingestJobId;

  const pollForQueryResult = async (queryId) => {
    const maxAttempts = 60;
    let consecutive404s = 0;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 2000));
      try {
        const res = await fetch(`${API_URL}/api/kb/query-result/${queryId}`);
        if (res.status === 404) {
          // Job lost from server memory (likely a restart between submit & poll).
          // Tolerate briefly — if it persists, throw a typed error so the caller can resubmit.
          consecutive404s += 1;
          if (consecutive404s >= 3) {
            const err = new Error('QUERY_JOB_LOST');
            err.code = 'QUERY_JOB_LOST';
            throw err;
          }
          continue;
        }
        consecutive404s = 0;
        if (!res.ok) throw new Error(`Poll error (${res.status})`);
        const data = await res.json();
        if ('ingestion_active' in data) setIngestionRunning(!!data.ingestion_active);
        if (data.status === 'completed' || data.status === 'error') return data;
      } catch (err) {
        if (err.code === 'QUERY_JOB_LOST') throw err;
        if (i >= 5) throw err;
      }
    }
    throw new Error('Query timed out after 2 minutes');
  };

  const submitQuery = async (question, chatHistory, _isRetry = false) => {
    const response = await fetch(`${API_URL}/api/kb/query`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question, password, session_id: sessionId, chat_history: chatHistory })
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || errData.detail || `Server error (${response.status})`);
    }
    const startData = await response.json();
    if ('ingestion_active' in startData) setIngestionRunning(!!startData.ingestion_active);
    try {
      return await pollForQueryResult(startData.query_id);
    } catch (err) {
      // If the job was lost (server restarted mid-query), resubmit ONCE transparently.
      if (err.code === 'QUERY_JOB_LOST' && !_isRetry) {
        console.warn('[chat] query job lost mid-poll, retrying once...');
        return await submitQuery(question, chatHistory, true);
      }
      throw err;
    }
  };

  const handleQuery = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isQuerying) return;
    const question = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsQuerying(true);
    try {
      const chatHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const data = await submitQuery(question, chatHistory);
      setSessionId(data.session_id);
      const newIdx = messages.length + 1;
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.answer,
        sources: data.sources || [], matched_documents: data.matched_documents || [], matched_members: data.matched_members || [],
        quick_actions: data.quick_actions || []
      }]);
      fetchRelatedQueries(question, newIdx);
    } catch (err) {
      const isNetwork = err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError');
      const isJobLost = err?.code === 'QUERY_JOB_LOST';
      const isServerOops = err?.message?.includes('Oops');
      let errorMsg;
      if (isJobLost) errorMsg = 'The server briefly restarted. Please try your question again — it should work now.';
      else if (isServerOops) errorMsg = err.message;  // pass through the friendly server-side message
      else if (isNetwork && ingestionRunning) errorMsg = 'The server is currently ingesting documents. Chat will resume shortly — please try again in a moment.';
      else if (isNetwork) errorMsg = 'Connection error. Please check your network and try again.';
      else errorMsg = 'Oops — something went wrong. The technical team has been notified. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, sources: [] }]);
    } finally { setIsQuerying(false); }
  };

  const sendQuickQuestion = (question) => {
    setActiveTab('chat');
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: question }]);
    setIsQuerying(true);
    const chatHistory = messages.map(m => ({ role: m.role, content: m.content }));
    submitQuery(question, chatHistory).then(data => {
      setSessionId(data.session_id);
      setMessages(prev => {
        const newIdx = prev.length;
        fetchRelatedQueries(question, newIdx);
        return [...prev, {
          role: 'assistant', content: data.answer,
          sources: data.sources || [], matched_documents: data.matched_documents || [], matched_members: data.matched_members || [],
          quick_actions: data.quick_actions || []
        }];
      });
    }).catch((err) => {
      const isNetwork = err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError');
      const isJobLost = err?.code === 'QUERY_JOB_LOST';
      const isServerOops = err?.message?.includes('Oops');
      let errorMsg;
      if (isJobLost) errorMsg = 'The server briefly restarted. Please try your question again — it should work now.';
      else if (isServerOops) errorMsg = err.message;
      else if (isNetwork && ingestionRunning) errorMsg = 'The server is currently ingesting documents. Chat will resume shortly — please try again in a moment.';
      else if (isNetwork) errorMsg = 'Connection error. Please check your network and try again.';
      else errorMsg = 'Oops — something went wrong. The technical team has been notified. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, sources: [] }]);
    }).finally(() => setIsQuerying(false));
  };

  // Retry the last question after an error — replaces the failed answer in place
  // (no duplicate user bubble).
  const retryLastQuery = async () => {
    if (isQuerying) return;
    const lastUserIdx = messages.map(m => m.role).lastIndexOf('user');
    if (lastUserIdx === -1) return;
    const question = messages[lastUserIdx].content;
    const chatHistory = messages.slice(0, lastUserIdx).map(m => ({ role: m.role, content: m.content }));
    setMessages(messages.slice(0, lastUserIdx + 1));  // drop the failed assistant message
    setIsQuerying(true);
    try {
      const data = await submitQuery(question, chatHistory);
      setSessionId(data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.answer,
        sources: data.sources || [], matched_documents: data.matched_documents || [], matched_members: data.matched_members || [],
        quick_actions: data.quick_actions || []
      }]);
    } catch (err) {
      const isNetwork = err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError');
      const errorMsg = isNetwork
        ? 'Still having trouble connecting. Please check your network and try again.'
        : 'That didn\'t go through. Please try again in a moment.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg, sources: [] }]);
    } finally { setIsQuerying(false); }
  };

  const handleFeedback = async (msgIdx, type) => {
    if (feedbackGiven[msgIdx]) return;
    setFeedbackGiven(prev => ({ ...prev, [msgIdx]: type }));
    try {
      const msg = messages[msgIdx];
      const userMsg = messages[msgIdx - 1];
      await fetch(`${API_URL}/api/kb/feedback`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, question: userMsg?.content || '', answer: msg?.content?.substring(0, 500) || '', feedback: type, timestamp: new Date().toISOString() })
      });
    } catch (e) { /* silent */ }
  };

  const startEdit = () => {
    setEditMode(true);
    setEditData({
      title: selectedDoc.title || '', summary: selectedDoc.summary || '',
      category: selectedDoc.category || '', year: selectedDoc.year || '',
      conference: selectedDoc.conference || '',
      authors: (selectedDoc.authors || []).join(', '),
      organizations: (selectedDoc.organizations || []).join(', '),
      key_topics: (selectedDoc.key_topics || []).join(', '),
      tags: (selectedDoc.tags || []).join(', ')
    });
  };

  const cancelEdit = () => { setEditMode(false); setEditData({}); setShowSavePrompt(false); };
  const triggerSave = () => { setShowSavePrompt(true); };

  const saveEdit = async () => {
    setSaving(true);
    setShowSavePrompt(false);
    try {
      const payload = { password, edited_by: editorName.trim() || undefined };
      if (editData.title !== selectedDoc.title) payload.title = editData.title;
      if (editData.summary !== selectedDoc.summary) payload.summary = editData.summary;
      if (editData.category !== selectedDoc.category) payload.category = editData.category;
      if ((editData.conference || '') !== (selectedDoc.conference || '')) payload.conference = editData.conference;
      const yearVal = editData.year ? parseInt(editData.year) : null;
      if (yearVal !== selectedDoc.year) payload.year = yearVal;
      const authArr = editData.authors.split(',').map(s => s.trim()).filter(Boolean);
      if (JSON.stringify(authArr) !== JSON.stringify(selectedDoc.authors || [])) payload.authors = authArr;
      const orgArr = editData.organizations.split(',').map(s => s.trim()).filter(Boolean);
      if (JSON.stringify(orgArr) !== JSON.stringify(selectedDoc.organizations || [])) payload.organizations = orgArr;
      const topicArr = editData.key_topics.split(',').map(s => s.trim()).filter(Boolean);
      if (JSON.stringify(topicArr) !== JSON.stringify(selectedDoc.key_topics || [])) payload.key_topics = topicArr;
      const tagArr = editData.tags.split(',').map(s => s.trim()).filter(Boolean);
      if (JSON.stringify(tagArr) !== JSON.stringify(selectedDoc.tags || [])) payload.tags = tagArr;
      const res = await fetch(`${API_URL}/api/kb/documents/${selectedDoc.doc_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedDoc(data.document);
        setEditMode(false);
        setEditData({});
        fetchDocuments();
        fetchFilters();
      }
    } catch (e) { console.error('Save failed:', e); }
    finally { setSaving(false); }
  };

  const handleAiRescan = async () => {
    setRescanning(true);
    try {
      const res = await fetch(`${API_URL}/api/kb/documents/${selectedDoc.doc_id}/rescan`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      if (res.ok) {
        const meta = await res.json();
        setEditData({
          title: meta.title || '', summary: meta.summary || '',
          category: meta.category || '', year: meta.year || '',
          authors: (meta.authors || []).join(', '),
          organizations: (meta.organizations || []).join(', '),
          key_topics: (meta.key_topics || []).join(', '),
          tags: (meta.tags || []).join(', ')
        });
      }
    } catch (e) { console.error('AI Rescan failed:', e); }
    finally { setRescanning(false); }
  };

  const handleView = async (r2Key) => {
    if (r2Key && r2Key.startsWith('http')) { window.open(r2Key, '_blank'); return; }
    const filename = r2Key.split('/').pop() || '';
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const browserViewable = ['pdf', 'png', 'jpg', 'jpeg', 'svg', 'txt', 'csv', 'md'].includes(ext);

    try {
      const formData = new FormData();
      formData.append('r2_key', r2Key);
      formData.append('password', password);
      const response = await fetch(`${API_URL}/api/kb/view-token`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        const proxyUrl = `${API_URL}/api/kb/view-file?r2_key=${encodeURIComponent(r2Key)}&token=${data.token}&expires=${data.expires}`;
        if (browserViewable) {
          // PDF, images, text — browser can render inline
          window.open(proxyUrl, '_blank');
        } else {
          // PPTX, DOCX, etc — use Google Docs Viewer for inline rendering
          const gdocsUrl = `https://docs.google.com/gview?url=${encodeURIComponent(proxyUrl)}&embedded=true`;
          window.open(gdocsUrl, '_blank');
        }
      }
    } catch (err) { console.error('View failed:', err); }
  };

  const handleDownload = async (r2Key) => {
    if (r2Key && r2Key.startsWith('http')) { window.open(r2Key, '_blank'); return; }
    try {
      const formData = new FormData();
      formData.append('r2_key', r2Key);
      formData.append('password', password);
      formData.append('mode', 'download');
      const response = await fetch(`${API_URL}/api/kb/download-url`, { method: 'POST', body: formData });
      if (response.ok) {
        const data = await response.json();
        const a = document.createElement('a');
        a.href = data.url;
        a.download = data.filename || r2Key.split('/').pop() || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) { console.error('Download failed:', err); }
  };

  const handleSuggestion = async (e) => {
    e.preventDefault();
    if (!suggestionText.trim() || sendingSuggestion) return;
    setSendingSuggestion(true);
    try {
      const res = await fetch(`${API_URL}/api/kb/suggestion`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: suggestionText.trim(), password, type: feedbackType })
      });
      if (res.ok) {
        setSuggestionSent(true);
        setSuggestionText('');
        setTimeout(() => { setSuggestionSent(false); setShowSuggestionBox(false); }, 2500);
      }
    } catch (err) { console.error('Suggestion failed:', err); }
    finally { setSendingSuggestion(false); }
  };

  const fetchAdminData = useCallback(async () => {
    setLoadingAdmin(true);
    try {
      const [logsRes, sugRes, syncRes, versionRes] = await Promise.all([
        fetch(`${API_URL}/api/kb/query-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) }),
        fetch(`${API_URL}/api/kb/suggestions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) }),
        fetch(`${API_URL}/api/kb/sync-logs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password }) }),
        fetch(`${API_URL}/api/kb/version`).catch(() => null)
      ]);
      if (logsRes.ok) { const d = await logsRes.json(); setQueryLogs(d.logs || []); }
      if (sugRes.ok) { const d = await sugRes.json(); setSuggestions(d.suggestions || []); }
      if (syncRes.ok) { const d = await syncRes.json(); setSyncLogs(d.logs || []); }
      if (versionRes?.ok) setBackendVersion(await versionRes.json());
      else setBackendVersion({ version: 'unknown', build: 'N/A', features: [] });
    } catch (err) { console.error('Admin fetch failed:', err); }
    finally { setLoadingAdmin(false); }
  }, [API_URL, password]);

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // --- Context value ---
  const kb = {
    // Auth
    isAuthenticated, password, setPassword, handlePasswordSubmit, passwordError, loginVersion,
    // Magic-link member auth (Phase 1C)
    memberInfo, magicEmail, setMagicEmail, magicSending, magicSent, setMagicSent,
    magicError, requestMagicLink, memberLogout, staffLogout,
    showLegacyPassword, setShowLegacyPassword,
    updateMemberProfile,
    showProfileModal, setShowProfileModal,
    adminFocus, setAdminFocus,
    // UI state
    activeTab, setActiveTab, lightMode, setLightMode, viewMode, setViewMode,
    showGuide, setShowGuide, showDisclaimerModal, setShowDisclaimerModal,
    appReady,
    // Chat
    messages, setMessages, inputValue, setInputValue, isQuerying,
    sessionId, setSessionId, handleQuery, sendQuickQuestion,
    ingestionRunning, chatEndRef, retryLastQuery,
    feedbackGiven, handleFeedback,
    expandedDocCards, setExpandedDocCards,
    relatedQueries,
    // Suggestion
    showSuggestionBox, setShowSuggestionBox, feedbackType, setFeedbackType,
    suggestionText, setSuggestionText, handleSuggestion, sendingSuggestion, suggestionSent,
    // Documents
    documents, loadingDocs, selectedDoc, setSelectedDoc,
    // Document edit
    editMode, setEditMode, editData, setEditData,
    saving, rescanning, showSavePrompt, setShowSavePrompt, editorName, setEditorName,
    startEdit, cancelEdit, triggerSave, saveEdit, handleAiRescan,
    // Filters
    filterOptions, searchQuery, setSearchQuery,
    activeCategories, toggleCategory,
    activeYears, toggleYear,
    activeFileTypes, toggleFileType,
    activeTopics, toggleTopic,
    showAllTopics, setShowAllTopics,
    hasActiveFilters, clearFilters,
    sortBy, setSortBy, sortOrder, setSortOrder,
    handleSearchSubmit, fetchDocuments,
    // Ingestion
    isIngesting, handleSyncNew, handleReingest,
    ingestJobId, ingestStatus, setIngestStatus, ingestDone, setIngestDone,
    // Trending
    trendingData, hotPicks, hotPicksIdx, refreshHotPicks,
    // Admin
    queryLogs, suggestions, syncLogs, loadingAdmin, backendVersion, fetchAdminData,
    adminFiles, setAdminFiles, adminUploading, adminUploadProgress,
    adminCapWarning, handleAdminFileSelect, handleAdminUpload,
    uploadConference, setUploadConference, uploadYear, setUploadYear,
    duplicates, deletePassword, setDeletePassword, deletingDocId, findDuplicates, handleDeleteDoc,
    // Utilities
    handleDownload, handleView, formatFileSize,
    setActiveCategories, setActiveYears, setActiveFileTypes, setActiveTopics
  };

  // --- Render ---
  if (!isAuthenticated) {
    return (
      <KBContext.Provider value={kb}>
        <div className={`kb-page ${lightMode ? 'kb-light' : ''}`} data-theme={visualTheme} data-testid="kb-page">
          <LoginScreen />
          <DisclaimerModal show={showDisclaimerModal} onClose={() => setShowDisclaimerModal(false)} />
        </div>
      </KBContext.Provider>
    );
  }

  if (!appReady) {
    return (
      <div className={`kb-page ${lightMode ? 'kb-light' : ''}`} data-theme={visualTheme} data-testid="kb-page-loading">
        <KBLoadingScreen />
      </div>
    );
  }

  return (
    <KBContext.Provider value={kb}>
      <div className={`kb-page ${lightMode ? 'kb-light' : ''}`} data-theme={visualTheme} data-testid="kb-page-main">
        <TopNav />
        <div className="kb-layout">
          <KBSidebar />
          <div className="kb-main">
            <AnimatePresence>
              {(ingestStatus || ingestDone) && (
                <IngestionProgress
                  ingestStatus={ingestStatus}
                  ingestDone={ingestDone}
                  setIngestDone={setIngestDone}
                  setIngestStatus={setIngestStatus}
                />
              )}
            </AnimatePresence>
            {activeTab === 'chat' && <ChatPanel />}
            {activeTab === 'documents' && (
              <div className="kb-docs-container" data-testid="kb-docs-container">
                <DocumentsPanel />
              </div>
            )}
            {activeTab === 'members' && <MembersPanel />}
            {/* Admin panel is staff/admin-only. Members (memberInfo set) must never
                see it, even if activeTab is somehow 'admin'. */}
            {activeTab === 'admin' && !memberInfo && <AdminPanel />}
          </div>
        </div>
        <DisclaimerModal show={showDisclaimerModal} onClose={() => setShowDisclaimerModal(false)} />
        <QuickGuideModal show={showGuide} onClose={() => setShowGuide(false)} />
        <ProfileEditModal show={showProfileModal} onClose={() => setShowProfileModal(false)} />
        <MemberWelcomeModal show={showMemberWelcome} memberName={memberInfo?.full_name} onClose={closeMemberWelcome} />
      </div>
    </KBContext.Provider>
  );
};

const KnowledgeBasePage = () => (
  <ThemeProvider>
    <KnowledgeBasePageInner />
  </ThemeProvider>
);

export default KnowledgeBasePage;
